<template>
  <InfuseTable
    :columns="columns"
    :rows="rowsWithReadonly"
    :active="(row: Role, id?: number) => active(row, id)"
    :loading="loading"
    :search="search"
    :has-more-items="hasMoreItems"
    :removable="removable"
    :editable="editable"
    @selected="(value: Role[]) => (selectedRows = value)"
    @search="(query: string) => emit('search', query)"
    @load="emit('load')"
    @click-row="executeClick"
    @remove="(row: Role) => emit('remove', row)"
    @edit="(row: Role) => emit('edit', row)"
    :remove-permission-id="rolePermissions.DELETE"
    :edit-permission-id="rolePermissions.UPDATE"
    :selectable="advancedEnabled"
    :show-selection-chips="advancedEnabled"
    :selection-chip-label="(row: Role) => row.name"
  >
    <template #actions>
      <InfuseTooltip
        v-if="hasAdvancedActions"
        v-permission="{ ids: [exportPermission, importPermission] }"
        :content="t('iam.roles.header.advanced_tooltip')"
        offset-distance="4"
      >
        <!-- A plain button rather than InfuseIconButton, which hardcodes its own text colour. -->
        <button
          type="button"
          data-testid="roles-advanced-toggle"
          class="rounded-md border p-2 transition-colors"
          :class="
            advanced
              ? 'border-primary-500 bg-primary-50 text-primary-500'
              : 'border-transparent text-neutral-400 hover:text-primary-500'
          "
          :aria-pressed="advanced"
          :aria-label="t('iam.roles.header.advanced')"
          @click="advanced = !advanced"
        >
          <font-awesome-icon :icon="['fas', 'gear']" />
        </button>
      </InfuseTooltip>

      <InfuseButton
        data-testid="page-create-button"
        class="flex w-64 items-center"
        size="medium"
        @click="emit('create')"
        v-permission="{ id: roleCreationPermission, hide: false }"
      >
        <font-awesome-icon :icon="['fas', 'plus']" class="mr-2" />
        {{ t('iam.roles.header.add_button') }}
      </InfuseButton>
    </template>

    <!--
      Bulk export and import get a row of their own: they are rarely used, each sits behind its own
      permission, and they do not fit alongside the search field and 'Add role'.
    -->
    <template v-if="advancedEnabled" #secondaryActions="{ selectedRows: selection }">
      <span class="text-xs text-neutral-500">{{ t('iam.roles.header.advanced_hint') }}</span>

      <InfuseButton
        v-if="exportPermission"
        data-testid="export-selected-roles-button"
        size="medium"
        color="secondary"
        :loading="exporting"
        :disabled="exporting || selection.length === 0"
        @click="emit('export', selection)"
        v-permission="{ id: exportPermission }"
      >
        <font-awesome-icon :icon="['far', 'arrow-down-to-line']" class="mr-2" />
        <!-- 'Export 0 selected' reads oddly, so the count only appears once there is one. -->
        {{
          selection.length === 0
            ? t('iam.roles.header.export_roles')
            : t('iam.roles.header.export_selected', { count: selection.length })
        }}
      </InfuseButton>

      <InfuseButton
        v-if="importPermission"
        data-testid="page-import-roles-button"
        size="medium"
        color="secondary"
        @click="emit('import')"
        v-permission="{ id: importPermission }"
      >
        <font-awesome-icon :icon="['far', 'arrow-up-from-line']" class="mr-2" />
        {{ t('iam.roles.header.import_roles') }}
      </InfuseButton>
    </template>

    <template #level="{ row }">
      <div class="flex flex-row gap-2">
        <RoleLevelTag :role="row" />
      </div>
    </template>

    <template #ranking="{ data, row }">
      <div class="flex flex-row gap-4">
        <div class="flex flex-row items-center gap-2">
          <font-awesome-icon :icon="getRankingIcon(data)" class="text-neutral-600" />
          <span v-if="editingRankingRowId !== row.id">{{ data }}</span>
          <input
            v-else
            ref="rankingInputRef"
            type="number"
            :value="editingRankingValue"
            @input="(e) => (editingRankingValue = Number((e.target as HTMLInputElement).value))"
            @keyup.enter="setRanking(row)"
            @blur="cancelEditRanking"
            @click.stop
            class="w-16 rounded border border-neutral-300 px-2 py-1 text-sm focus:border-primary-500 focus:outline-none"
            :disabled="loadingRanking"
            min="1"
          />
        </div>
        <div class="flex flex-row invisible group-hover:visible" v-if="!row.readonly">
          <font-awesome-icon
            v-if="!loadingRanking && editingRankingRowId !== row.id"
            :icon="['far', 'pen']"
            class="px-2 text-neutral-400 hover:text-primary-500"
            @click.stop="startEditRanking(row, data)"
            v-permission="{
              ids: [
                rolePermissions.SET_RANKING,
                tenantRolePermissions.SET_RANKING,
                organisationRolePermissions.SET_RANKING
              ],
              hide: false
            }"
          />
          <font-awesome-icon
            v-if="!loadingRanking && editingRankingRowId !== row.id"
            :icon="['far', 'arrow-down']"
            class="px-2 text-neutral-400 hover:text-primary-500"
            @click.stop="emit('demote', row)"
            v-permission="{
              ids: [rolePermissions.DEMOTE, tenantRolePermissions.DEMOTE, organisationRolePermissions.DEMOTE],
              hide: false
            }"
          />
          <font-awesome-icon
            v-if="!loadingRanking && data > 1 && editingRankingRowId !== row.id"
            :icon="['far', 'arrow-up']"
            class="px-2 text-neutral-400 hover:text-primary-500"
            @click.stop="emit('promote', row)"
            v-permission="{
              ids: [rolePermissions.PROMOTE, tenantRolePermissions.PROMOTE, organisationRolePermissions.PROMOTE],
              hide: false
            }"
          />

          <font-awesome-icon v-if="loadingRanking" :icon="['far', 'spinner']" class="px-5" spin-pulse />
        </div>
      </div>
    </template>

    <template #createdAt="{ data }">
      {{ formatDate(data) }}
    </template>

    <template #modifiedAt="{ data }">
      {{ formatDate(data) }}
    </template>

    <template #scope="{ row }">
      <InfuseTooltip :content="getScopeTooltip(row, t)" offset-distance="4">
        <InfuseTag :inline="true">
          {{ getScopeText(row, t) }}
        </InfuseTag>
      </InfuseTooltip>
    </template>
  </InfuseTable>
</template>

<script lang="ts" setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { rolePermissions } from '@infuse/api/iam/role';
import { vPermission } from '@infuse/iam/permission/directives';
import { RoleLevelTag } from '@infuse/iam/role/ui';
import { Role, RoleLevel } from '@infuse/iam/role/type';
import { getRankingIcon, getScopeText, getScopeTooltip, isRoleSharedToThisLevel } from './roleScope';
import { InfuseButton, InfuseTable, InfuseTag, InfuseTooltip } from '@infuse/ui/components';
import type { UiTableColumn } from '@infuse/ui/domain';
import { formatDate } from '@infuse/ui/utils';
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { tenantRolePermissions } from '@infuse/api/iam/tenant';
import { organisationRolePermissions } from '@infuse/api/iam/organisation';

const { t } = useI18n();

interface Emits {
  (e: 'load'): void;

  (e: 'clickRow', row: Role): void;

  (e: 'search', query: string): void;

  (e: 'remove', row: Role): void;

  (e: 'edit', row: Role): void;

  (e: 'promote', row: Role): void;

  (e: 'demote', row: Role): void;

  (e: 'set-ranking', row: Role, ranking: number): void;

  (e: 'create'): void;

  (e: 'import'): void;

  (e: 'export', rows: Role[]): void;
}

const emit = defineEmits<Emits>();
const selectedRows = ref<Role[]>([]);
const advanced = ref(false);
const editingRankingRowId = ref<string | null>(null);
const editingRankingValue = ref<number>(0);
const rankingInputRef = ref<HTMLInputElement | null>(null);

interface Props {
  rows: Role[];
  active?: (row: Role, id?: number) => boolean;
  loading: boolean;
  loadingRanking?: boolean;
  page: number;
  searchQuery?: string;
  searchEnabled: boolean;
  removable?: boolean;
  editable?: boolean;
  level?: RoleLevel;
  hasMoreItems: boolean;
  roleCreationPermission?: string;
  /** Turns on row selection, which the multi-role export needs. */
  selectable?: boolean;
  /** When set, the export action over a selection is shown to users holding it. */
  exportPermission?: string;
  /** When set, the import action is shown in the toolbar to users holding it. */
  importPermission?: string;
  exporting?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  rows: () => [],
  active: () => false,
  loading: false,
  searchQuery: '',
  searchEnabled: true,
  removable: false,
  editable: false,
  hasMoreItems: true,
  roleCreationPermission: rolePermissions.CREATE,
  selectable: false,
  exportPermission: undefined,
  importPermission: undefined,
  exporting: false
});

// The gear only earns its place when there is something behind it to reveal.
const hasAdvancedActions = computed(() => props.selectable && !!(props.exportPermission || props.importPermission));

// Row selection exists purely to feed the bulk export, so it appears with the advanced actions.
const advancedEnabled = computed(() => hasAdvancedActions.value && advanced.value);

const search = computed(() =>
  !props.searchEnabled
    ? undefined
    : {
        placeholder: t('iam.roles.search_placeholder'),
        query: props.searchQuery
      }
);

const rowsWithReadonly = computed(() => {
  return props.rows.map((row) => {
    return {
      ...row,
      readonly: isRoleSharedToThisLevel(row, props.level)
    };
  });
});

function startEditRanking(row: Role, currentRanking: number): void {
  editingRankingRowId.value = row.id;
  editingRankingValue.value = currentRanking;
  nextTick(() => {
    rankingInputRef.value?.focus();
    rankingInputRef.value?.select();
  });
}

function setRanking(row: Role): void {
  if (editingRankingValue.value > 0) {
    emit('set-ranking', row, editingRankingValue.value);
    editingRankingRowId.value = null;
  }
}

function cancelEditRanking(): void {
  editingRankingRowId.value = null;
}

function executeClick(row: Role & { readonly?: boolean }): void {
  if (!row.readonly) {
    emit('clickRow', row);
  }
}

const columns: UiTableColumn[] = [
  {
    name: 'name',
    label: () => t('iam.roles.name'),
    field: 'name',
    bold: true
  },
  {
    name: 'description',
    label: () => t('iam.roles.description'),
    field: 'description'
  },
  {
    name: 'level',
    label: () => t('iam.roles.level'),
    field: 'level'
  },
  {
    name: 'scope',
    label: () => t('iam.roles.scope'),
    field: 'scope'
  },
  {
    name: 'userCount',
    label: () => t('iam.roles.userCount'),
    field: 'userCount'
  },
  {
    name: 'ranking',
    label: () => t('iam.roles.ranking'),
    field: 'ranking'
  },
  {
    name: 'createdAt',
    label: () => t('iam.roles.createdAt'),
    field: 'createdAt'
  },
  {
    name: 'modifiedAt',
    label: () => t('iam.roles.modifiedAt'),
    field: 'modifiedAt'
  }
];
</script>
